/**
 * GradingPage Component
 * Main page for grading student submissions with AI-assisted feedback.
 * Uses a scrollable PDF viewer with document-level AI feedback.
 */
import React, { useCallback, useState, useMemo } from 'react';
import { MainLayout, Sidebar } from '../components/Layout';
import PDFViewer from '../components/PDFViewer';
import FileUpload from '../components/Upload/FileUpload';
import { Highlight } from '../components/CommentOverlay';
import { EditModal, RejectModal, CategoryDismissModal, AddCommentModal } from '../components/Modals';
import { usePDF, useComments, useKeyboardShortcuts, useTextSelection } from '../hooks';
import { useToast } from '../components/Toast';
import KeyboardShortcutHint from '../components/KeyboardShortcutHint';
import type { Comment } from '../types';

const GradingPage: React.FC = () => {
  const {
    submissionId,
    filename,
    loading: pdfLoading,
    error: pdfError,
    uploadFile,
    getAllPagesText,
  } = usePDF();

  const {
    comments,
    loading: commentsLoading,
    error: commentsError,
    selectedCommentId,
    generateForDocument,
    acceptComment,
    rejectComment,
    editComment,
    dismissCategory,
    createManualComment,
    selectComment,
    getCommentsForPage,
    clearComments,
  } = useComments();

  // Text selection for manual comments
  const { selection: textSelection, clearSelection } = useTextSelection();

  // Toast notifications
  const { showToast } = useToast();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Modal state
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [rejectingComment, setRejectingComment] = useState<Comment | null>(null);
  const [dismissingCategory, setDismissingCategory] = useState<{
    category: string;
    count: number;
  } | null>(null);
  const [addingComment, setAddingComment] = useState<{
    text: string;
    pageNumber: number;
    position: { x: number; y: number; width: number; height: number };
  } | null>(null);

  // Get currently selected comment
  const selectedComment = useMemo(
    () => comments.find((c) => c.id === selectedCommentId) || null,
    [comments, selectedCommentId]
  );

  // Get pending comments for navigation
  const pendingComments = useMemo(
    () =>
      [...comments]
        .filter((c) => c.status === 'pending')
        .sort((a, b) => {
          if (a.pageNumber !== b.pageNumber) return a.pageNumber - b.pageNumber;
          return a.position.y - b.position.y;
        }),
    [comments]
  );

  // Navigate to next/prev comment
  const goToNextComment = useCallback(() => {
    if (pendingComments.length === 0) return;

    const currentIndex = selectedCommentId
      ? pendingComments.findIndex((c) => c.id === selectedCommentId)
      : -1;

    const nextIndex =
      currentIndex < pendingComments.length - 1 ? currentIndex + 1 : 0;
    selectComment(pendingComments[nextIndex].id);
  }, [pendingComments, selectedCommentId, selectComment]);

  const goToPrevComment = useCallback(() => {
    if (pendingComments.length === 0) return;

    const currentIndex = selectedCommentId
      ? pendingComments.findIndex((c) => c.id === selectedCommentId)
      : pendingComments.length;

    const prevIndex =
      currentIndex > 0 ? currentIndex - 1 : pendingComments.length - 1;
    selectComment(pendingComments[prevIndex].id);
  }, [pendingComments, selectedCommentId, selectComment]);

  // Handle file upload
  const handleUpload = useCallback(
    async (file: File) => {
      // Clear any existing comments
      clearComments();

      try {
        // Upload to backend first
        await uploadFile(file);

        // Only show PDF viewer after successful upload
        const url = URL.createObjectURL(file);
        setPdfUrl(url);
      } catch {
        // Error is already set in usePDF hook and displayed on upload screen
      }
    },
    [uploadFile, clearComments]
  );

  // Handle new document
  const handleNewDocument = useCallback(() => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    clearComments();
    window.location.reload();
  }, [pdfUrl, clearComments]);

  // Handle generate feedback for entire document
  const handleGenerateFeedback = useCallback(async () => {
    if (!submissionId) {
      showToast('warning', 'Please upload a PDF first');
      return;
    }

    // Get all pages text
    const pages = await getAllPagesText();
    if (pages.length === 0) {
      showToast('warning', 'No text content found in document');
      return;
    }

    try {
      await generateForDocument({
        submission_id: submissionId,
        teacher_id: '00000000-0000-0000-0000-000000000001', // TODO: Get from auth
        pages,
      });
      showToast('success', 'Feedback generated successfully');
    } catch {
      showToast('error', 'Failed to generate feedback');
    }
  }, [submissionId, getAllPagesText, generateForDocument, showToast]);

  // Handle comment actions
  const handleAccept = useCallback(
    async (id: string) => {
      try {
        await acceptComment(id);
        showToast('success', 'Comment accepted');
      } catch {
        showToast('error', 'Failed to accept comment');
      }
    },
    [acceptComment, showToast]
  );

  const handleReject = useCallback(
    (id: string) => {
      const comment = comments.find((c) => c.id === id);
      if (comment) {
        setRejectingComment(comment);
      }
    },
    [comments]
  );

  const handleEdit = useCallback(
    (id: string) => {
      const comment = comments.find((c) => c.id === id);
      if (comment) {
        setEditingComment(comment);
      }
    },
    [comments]
  );

  const handleDismissCategory = useCallback(
    (category: string) => {
      if (!submissionId) return;

      // Count affected comments
      const count = comments.filter(
        (c) =>
          c.submissionId === submissionId &&
          c.category === category &&
          c.status === 'pending'
      ).length;

      setDismissingCategory({ category, count });
    },
    [comments, submissionId]
  );

  // Handle opening the add comment modal
  const handleOpenAddComment = useCallback(() => {
    if (textSelection) {
      setAddingComment({
        text: textSelection.text,
        pageNumber: textSelection.pageNumber,
        position: textSelection.position,
      });
      clearSelection();
    }
  }, [textSelection, clearSelection]);

  // Handle saving a manual comment
  const handleSaveManualComment = useCallback(
    async (suggestion: string, category: string) => {
      if (!submissionId || !addingComment) return;

      try {
        await createManualComment(
          submissionId,
          addingComment.pageNumber,
          addingComment.text,
          suggestion,
          category,
          addingComment.position
        );
        showToast('success', 'Comment added');
      } catch {
        showToast('error', 'Failed to add comment');
      }
    },
    [submissionId, addingComment, createManualComment, showToast]
  );

  // Wrapper handlers with toast notifications for modals
  const handleEditSave = useCallback(
    async (commentId: string, newText: string) => {
      try {
        await editComment(commentId, newText);
        showToast('success', 'Comment edited');
      } catch {
        showToast('error', 'Failed to edit comment');
      }
    },
    [editComment, showToast]
  );

  const handleRejectSave = useCallback(
    async (commentId: string, reason: string) => {
      try {
        await rejectComment(commentId, reason);
        showToast('info', 'Comment rejected');
      } catch {
        showToast('error', 'Failed to reject comment');
      }
    },
    [rejectComment, showToast]
  );

  const handleDismissSave = useCallback(
    async (submissionIdArg: string, category: string) => {
      try {
        const count = await dismissCategory(submissionIdArg, category);
        showToast('info', `Dismissed ${count} comment${count !== 1 ? 's' : ''}`);
        return count;
      } catch {
        showToast('error', 'Failed to dismiss category');
        throw new Error('Failed to dismiss category');
      }
    },
    [dismissCategory, showToast]
  );

  // Check if any modal is open
  const isModalOpen = !!(editingComment || rejectingComment || dismissingCategory || addingComment);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    activeComment:
      selectedComment?.status === 'pending' ? selectedComment : null,
    handlers: {
      accept: () => {
        if (selectedCommentId) handleAccept(selectedCommentId);
      },
      edit: () => {
        if (selectedCommentId) handleEdit(selectedCommentId);
      },
      reject: () => {
        if (selectedCommentId) handleReject(selectedCommentId);
      },
      nextComment: goToNextComment,
      prevComment: goToPrevComment,
    },
    enabled: !!pdfUrl,
    modalOpen: isModalOpen,
  });

  // Handle highlight click - scroll to comment in sidebar
  const handleHighlightClick = useCallback(
    (comment: Comment) => {
      selectComment(comment.id);
    },
    [selectComment]
  );

  // Render page overlay with highlights
  const renderPageOverlay = useCallback(
    (pageNumber: number, width: number, height: number) => {
      const pageComments = getCommentsForPage(pageNumber).filter(
        (c) => c.status !== 'rejected'
      );

      // Render highlights - use positions array if available for multi-line
      return pageComments.flatMap((comment) => {
        const positionsToRender = comment.positions || [comment.position];

        return positionsToRender.map((pos, idx) => (
          <Highlight
            key={`${comment.id}-${idx}`}
            position={pos}
            confidence={comment.confidence}
            status={comment.status}
            isSelected={selectedCommentId === comment.id}
            onClick={() => handleHighlightClick(comment)}
            pageWidth={width}
            pageHeight={height}
          />
        ));
      });
    },
    [getCommentsForPage, selectedCommentId, handleHighlightClick]
  );

  // All comments for sidebar (sorted by page)
  const allComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      if (a.pageNumber !== b.pageNumber) {
        return a.pageNumber - b.pageNumber;
      }
      return a.position.y - b.position.y;
    });
  }, [comments]);

  // Render upload screen if no PDF
  if (!pdfUrl) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full p-8">
          <div className="max-w-lg w-full">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
              Upload a Document
            </h2>
            <p className="text-gray-500 mb-6 text-center">
              Upload a PDF to start grading with AI-assisted feedback.
            </p>
            <FileUpload onUpload={handleUpload} isLoading={pdfLoading} />
            {pdfError && (
              <p className="mt-4 text-sm text-red-600 text-center">{pdfError}</p>
            )}
          </div>
        </div>
      </MainLayout>
    );
  }

  // Render grading interface
  return (
    <MainLayout
      filename={filename || undefined}
      onNewDocument={handleNewDocument}
      sidebar={
        <Sidebar
          comments={allComments}
          selectedCommentId={selectedCommentId || undefined}
          onAccept={handleAccept}
          onReject={handleReject}
          onEdit={handleEdit}
          onDismissCategory={handleDismissCategory}
          onSelectComment={selectComment}
          onGenerateFeedback={handleGenerateFeedback}
          isGenerating={commentsLoading}
        />
      }
    >
      <PDFViewer
        fileUrl={pdfUrl}
        onDocumentLoad={() => {}}
        className="h-full"
        renderPageOverlay={renderPageOverlay}
      />

      {/* Error display */}
      {commentsError && (
        <div className="absolute bottom-4 left-4 bg-red-100 text-red-700 px-4 py-2 rounded-md shadow animate-fade-in">
          {commentsError}
        </div>
      )}

      {/* Edit Modal */}
      {editingComment && (
        <EditModal
          comment={editingComment}
          isOpen={!!editingComment}
          onSave={handleEditSave}
          onClose={() => setEditingComment(null)}
        />
      )}

      {/* Reject Modal */}
      {rejectingComment && (
        <RejectModal
          comment={rejectingComment}
          isOpen={!!rejectingComment}
          onReject={handleRejectSave}
          onClose={() => setRejectingComment(null)}
        />
      )}

      {/* Category Dismiss Modal */}
      {dismissingCategory && submissionId && (
        <CategoryDismissModal
          category={dismissingCategory.category}
          submissionId={submissionId}
          affectedCount={dismissingCategory.count}
          isOpen={!!dismissingCategory}
          onDismiss={handleDismissSave}
          onClose={() => setDismissingCategory(null)}
        />
      )}

      {/* Add Comment Modal */}
      {addingComment && (
        <AddCommentModal
          isOpen={!!addingComment}
          highlightedText={addingComment.text}
          pageNumber={addingComment.pageNumber}
          position={addingComment.position}
          onSave={handleSaveManualComment}
          onClose={() => setAddingComment(null)}
        />
      )}

      {/* Floating Add Comment Button (appears when text is selected) */}
      {textSelection && !isModalOpen && (
        <div
          className="fixed z-40 bg-white rounded-lg shadow-lg border border-gray-200 p-2 animate-fade-in"
          style={{
            left: Math.min(
              textSelection.rect.left + textSelection.rect.width / 2 - 60,
              window.innerWidth - 140
            ),
            top: Math.max(textSelection.rect.bottom + 8, 8),
          }}
        >
          <button
            onClick={handleOpenAddComment}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Comment
          </button>
        </div>
      )}

      {/* Keyboard shortcut feedback popup */}
      <KeyboardShortcutHint visible={!!pdfUrl && !isModalOpen} />
    </MainLayout>
  );
};

export default GradingPage;
